import React, { useState } from "react";
import clsx from "clsx";
import Badge from "../../../../../../shared-components/css-modules/lexus/atoms/Badge";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import List from "../../../../../../shared-components/css-modules/lexus/atoms/List";
import Icon from "../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import Incentive from "../../../../../../shared-components/css-modules/lexus/molecules/Incentive";
import styles from "./Packs.module.scss";
import Compare from "../../compare/Compare";
import SidePanel from "../../../../../../shared-components/css-modules/lexus/organisms/SidePanel";
import type { PackType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import Price from "../../../common/price/Price";
import useAphLbxPack from "../../../../../hooks/lbx/useAphLbxPack";
import { useCarConfigSelector } from "../../../../../redux/store";
import { getCarDBAssetSrcSet } from "../../../../../../../shared-logic/utils/imageUtils";
import {
    trackOptionsBasketPackAdd,
    trackOptionsBasketPackRemove,
    trackViewPackFeatures,
} from "../../../../../utils/tracking";
import { scrollToTarget } from "../../../../../../../../common-deprecated/utils/scrollUtils";
import { CarConfigConfigureSectionsLbx } from "../../../../../utils/types/SectionTypesLbx";
import { showAccessoriesRate } from "../../../../../utils/financeInsuranceRate";
import { CarConfigLabel } from "../../../../../utils/labelUtils";

export type Props = {
    index: number;
    pack: PackType;
};

const Pack: React.FC<Props> = (props): JSX.Element | null => {
    const currentGradeId = useCarConfigSelector((state) => state.car.grade.id);
    const { index, pack } = props;
    const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
    const packProps = useAphLbxPack(pack);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);

    if (!packProps) return null;

    const { actions, packContent } = packProps;
    const { selectPack, deselectPack, displayNotifications } = actions;
    const { supportedWheeldrives, includedPacks, selected, includedInPacks } = packContent;

    let wheelDrive = null;
    const hasFrontWeelDrive = supportedWheeldrives?.find(({ code }) => code === "2WD");
    const hasAllWeelDrive = supportedWheeldrives?.find(({ code }) => code === "4WD");
    if (hasFrontWeelDrive && hasAllWeelDrive) {
        wheelDrive = <CarConfigLabel label="carConfigLbxGradeWheeldriveFrontOrAll" />;
    } else if (hasFrontWeelDrive) {
        wheelDrive = <CarConfigLabel label="carConfigLbxGradeWheeldriveFront" />;
    } else if (hasAllWeelDrive) {
        wheelDrive = <CarConfigLabel label="carConfigLbxGradeWheeldriveAll" />;
    }

    const handleClick = (): void => {
        if (!pack.standard) {
            if (selected) {
                trackOptionsBasketPackRemove();
                deselectPack();
            } else {
                trackOptionsBasketPackAdd();
                selectPack();
                displayNotifications();
            }
        } else scrollToTarget(CarConfigConfigureSectionsLbx.Powertrain);
    };

    return (
        <article
            className={clsx(styles.pack, selected && styles.active)}
            style={{
                "--or-animation-delay": `${(index + 1) * 100 + 4}`,
            }}
        >
            <div className={styles["pack-image"]}>
                {/*INFO: Always keep the badge-holder wrapper ven when no badge is available*/}
                {/*/!*Badge options: base, premium, advanced*!/*/}
                {/*<Badge variant="premium">*/}
                {/*    /!*Other translations have been provided*!/*/}
                {/*    <CarConfigLabel useHTML label="carConfigPacksPremium" />*/}
                {/*</Badge>*/}
                <div className={styles["pack-badge-holder"]}>
                    {includedPacks.length === 0 && (
                        <Badge variant="base">
                            <CarConfigLabel label="carConfigLbxPacksBase" />
                        </Badge>
                    )}
                    {includedPacks.length === 1 && (
                        <Badge variant="premium">
                            <CarConfigLabel
                                label="carConfigLbxPacksPremium"
                                params={{ pack: includedPacks.map((includedPack) => includedPack.name).toString() }}
                                useHTML
                            />
                        </Badge>
                    )}
                    {includedPacks.length > 1 && (
                        <Badge variant="advanced">
                            <CarConfigLabel
                                label="carConfigLbxPacksAdvanced"
                                params={{
                                    packs: includedPacks
                                        .map((includedPack) => includedPack.name)
                                        .toString()
                                        .replace(",", " & "),
                                }}
                                useHTML
                            />
                        </Badge>
                    )}
                </div>
                {pack.equipment.slice(0, 1).map((equipment) => (
                    <figure key={equipment.id}>
                        <img
                            {...getCarDBAssetSrcSet(
                                commonSettings,
                                [
                                    { width: 180, height: 180 },
                                    { width: 225, height: 225 },
                                    { width: 336, height: 336 },
                                    { width: 386, height: 386 },
                                    { width: 426, height: 426 },
                                    { width: 772, height: 772 },
                                    { width: 852, height: 852 },
                                ],
                                equipment.image,
                                true,
                            )}
                            width="90"
                            height="90"
                            sizes="(max-width: 991px) 40vw, (max-width: 1199px) 33vw, 450px"
                            alt=""
                            role="presentation"
                        />
                        <figcaption>{equipment.name}</figcaption>
                    </figure>
                ))}
            </div>
            {pack.subtitle && <Incentive label={pack.subtitle} variant="best-seller" className={styles.highlighted} />}
            <header className={styles["pack-header"]}>
                <Text tag="h3" variant="apheleia-h4" className={styles["pack-title"]}>
                    {pack.name}
                </Text>

                <Text tag="div" variant="apheleia-body" className={styles.price}>
                    <Price
                        price={pack.price}
                        forceCashPrice={!showAccessoriesRate(commonSettings, carConfigSettings)}
                    />
                </Text>
            </header>
            <div className={styles["pack-info"]}>
                <dl>
                    <dt>
                        <CarConfigLabel label="carConfigLbxSupportedPowertrainPacks" />
                    </dt>
                    <dd>{wheelDrive}</dd>

                    <dt>
                        <CarConfigLabel
                            label="carConfigLbxPacksFeatureCount"
                            params={{ count: pack.equipment.length }}
                        />
                    </dt>
                    <dd>
                        <List tag="ul" className={styles.list}>
                            {pack.equipment.slice(1, 4).map((equipment) => (
                                <li key={equipment.id}>{equipment.name}</li>
                            ))}
                        </List>
                    </dd>
                </dl>
            </div>
            <footer className={styles["pack-footer"]}>
                <Button
                    variant="btn-text-upper"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        trackViewPackFeatures();
                        setIsSidePanelOpen(true);
                    }}
                >
                    <CarConfigLabel label="carConfigLbxPacksViewAll" />
                </Button>
                <Button
                    variant="btn-lbx"
                    sizing="full-width"
                    className={clsx(styles["pack-button"], selected && styles.active)}
                    onClick={handleClick}
                    disabled={pack.standard}
                >
                    <Icon
                        variant={selected ? "minus" : "plus"}
                        sizing="xs"
                        aria-hidden="true"
                        className={styles["pack-button-icon"]}
                    />

                    <CarConfigLabel label={selected ? "carConfigLbxRemove" : "carConfigLbxSelect"} />
                </Button>
                {pack.standard ? (
                    <Button className={styles.included} variant="btn-text" onClick={handleClick}>
                        <CarConfigLabel label="carConfigLbxIncludedInPowertrain" />
                    </Button>
                ) : (
                    includedInPacks.length > 0 && (
                        <div className={styles.included}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 25 24">
                                <rect width="23" height="23" x="1" y=".5" stroke="#B0B8C0" rx="11.5" />
                                <path
                                    fill="#101010"
                                    fillRule="evenodd"
                                    d="M18.5 8.456 10.433 16 6.5 12.322l.487-.455 3.446 3.222L18.013 8l.487.456Z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <CarConfigLabel
                                label="carConfigLbxIncludedInPack"
                                params={{
                                    pack: includedInPacks
                                        .map((includedPack) => includedPack.name)
                                        .toString()
                                        .replace(",", " & "),
                                }}
                            />
                        </div>
                    )
                )}
            </footer>
            <SidePanel
                variant="lbx-compare"
                open={isSidePanelOpen}
                onRequestClose={() => {
                    setIsSidePanelOpen(false);
                }}
            >
                <Compare initialGradeId={currentGradeId} openPacksByDefault />
            </SidePanel>
        </article>
    );
};

export default Pack;
